import { useState, useEffect } from 'react';
import { getJoueurs, createJoueur, updateJoueur, deleteJoueur, creerCompteJoueur, resetPasswordJoueur,
         getMaillotsJoueur, addLavage, deleteLavage, hasRole, getCurrentUser, getContexteMaillots } from '../api';

const POSTES = ['Gardien','Ailier Gauche','Ailier Droit','Pivot','Arrière Gauche','Arrière Droit','Demi-Centre'];
const ROLES = [
  { key:'joueur',     niveau:1, label:'Joueur',       desc:'Ses convocations, ses stats, sa situation dans les caisses', color:'#22c55e' },
  { key:'tresorier',  niveau:2, label:'Trésorier',    desc:'+ Gages d\'équipe et cagnotte de 3ème mi-temps',           color:'#eab308' },
  { key:'coach',      niveau:3, label:'Coach',        desc:'+ Effectif, convocations, présences, import des feuilles', color:'#3b82f6' },
  { key:'admin_club', niveau:4, label:'Admin du club', desc:'+ Toutes les équipes du club',                             color:'#a855f7' },
];
// Anciens noms, conservés pour l'affichage des comptes non encore reconnectés
const ALIAS = { admin:'admin_club', admin_3mt:'tresorier' };
const NIVEAUX = { super_admin:5, admin_club:4, admin:4, coach:3, tresorier:2, admin_3mt:2, joueur:1 };
/** Moins de 15 ans : le compte est rattaché à un responsable légal. */
function moinsDe15(dateNaissance) {
  if (!dateNaissance) return false;
  const n = new Date(dateNaissance), auj = new Date();
  let a = auj.getFullYear() - n.getFullYear();
  if (auj < new Date(auj.getFullYear(), n.getMonth(), n.getDate())) a--;
  return a < 15;
}
const roleInfo = r => r === 'super_admin'
  ? { key:'super_admin', label:'Éditeur', color:'#e63946' }
  : ROLES.find(x => x.key === (ALIAS[r] || r));

/**
 * Rôles que l'utilisateur peut attribuer — même règle que le serveur :
 * un rôle inférieur au sien, sauf l'admin du club qui peut nommer un pair.
 */
function rolesAttribuables(monRole) {
  if (monRole === 'super_admin') return ROLES;
  const moi = NIVEAUX[monRole] || 0;
  const estAdminClub = monRole === 'admin_club' || monRole === 'admin';
  return ROLES.filter(r => estAdminClub ? r.niveau <= moi : r.niveau < moi);
}

export default function JoueursPage() {
  const [joueurs, setJoueurs]   = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState({});
  const [created, setCreated]   = useState(null);
  const [toast, setToast]       = useState(null);
  const [showInactifs, setShowInactifs] = useState(false);
  const [vue, setVue]           = useState('effectif');
  const [selMaillot, setSelMaillot] = useState(null);
  const [lavages, setLavages]   = useState([]);
  const [ctxMaillots, setCtxMaillots] = useState(null);   // match concerné et joueurs présents
  const [autreChoix, setAutreChoix]   = useState(false);
  const isAdmin  = hasRole('coach');
  const isSuper  = getCurrentUser()?.role === 'super_admin';
  const attribuables = rolesAttribuables(getCurrentUser()?.role);

  useEffect(() => { load(); }, []);
  async function load() { setJoueurs(await getJoueurs()); }
  function showToast(msg, type='success') { setToast({msg,type}); setTimeout(()=>setToast(null),3000); }

  function openNew() {
    setEditing(null);
    setForm({ nom:'', prenom:'', numero:'', poste:'', poste2:'', telephone:'', email:'', email_parent:'', date_naissance:'', role:'joueur', creer_compte:true });
    setCreated(null); setShowForm(true);
  }
  function openEdit(j) {
    if (showForm && editing?.id === j.id) { setShowForm(false); setEditing(null); return; }   // second clic : on referme
    setEditing(j);
    setTimeout(() => document.getElementById(`joueur-${j.id}`)?.scrollIntoView({ behavior:'smooth', block:'nearest' }), 60);
    setForm({ nom:j.nom, prenom:j.prenom, numero:j.numero||'', poste:j.poste||'', poste2:j.poste2||'', telephone:j.telephone||'',
              email:j.email||'', email_parent:j.email_parent||'', date_naissance:j.date_naissance||'', role:j.role||'joueur', compte_actif:j.compte_actif });
    setCreated(null); setShowForm(true);
  }

  async function submit(e) {
    e.preventDefault();
    try {
      if (editing) {
        await updateJoueur(editing.id, form);
        showToast('Joueur mis à jour'); setShowForm(false);
      } else {
        const r = await createJoueur(form);
        if (r.compte) { setCreated({ ...r.compte, prenom:form.prenom, email:form.email }); }
        else { setShowForm(false); }
        showToast('Joueur ajouté');
      }
      load();
    } catch(err) { showToast(err.message, 'error'); }
  }

  async function creerCompte(j) {
    if (!j.email) return showToast('Renseigne d\'abord un email', 'error');
    try {
      const r = await creerCompteJoueur(j.id, { role:'joueur' });
      setCreated({ ...r.compte, prenom:j.prenom, email:j.email });
      showToast('Compte créé'); load();
    } catch(err) { showToast(err.message, 'error'); }
  }

  async function resetMdp(j) {
    if (!window.confirm(`Réinitialiser le mot de passe de ${j.prenom} ?`)) return;
    try {
      const r = await resetPasswordJoueur(j.id);
      setCreated({ ...r.compte, prenom:j.prenom, email:j.email, reset:true });
      showToast('Mot de passe réinitialisé');
    } catch(err) { showToast(err.message, 'error'); }
  }

  async function supprimer(j) {
    if (!window.confirm(`Retirer ${j.prenom} ${j.nom} de l'effectif ?\n\nSes statistiques et son historique sont conservés. Tu pourras le réactiver plus tard.`)) return;
    try { await deleteJoueur(j.id); showToast('Joueur retiré de l\'effectif'); load(); }
    catch(err) { showToast(err.message, 'error'); }
  }

  async function reactiver(j) {
    try { await updateJoueur(j.id, { ...j, actif:1, compte_actif:1 }); showToast('Joueur réactivé'); load(); }
    catch(err) { showToast(err.message, 'error'); }
  }

  async function openMaillots(j) {
    if (selMaillot === j.id) return setSelMaillot(null);
    setSelMaillot(j.id);
    setLavages(await getMaillotsJoueur(j.id));
  }

  async function ajouterLavage(j) {
    try {
      const r = ctxMaillots?.rencontre;
      // Match du jour ou passé : le tour est daté du match ; match à venir : daté d'aujourd'hui
      await addLavage(j.id, r ? { ...(r.passe || r.aujourdhui ? { date: r.date } : {}), note: `vs ${r.adversaire}` } : {});
      setAutreChoix(false);
      showToast(`Tour ajouté pour ${j.prenom}`);
      load();
      if (selMaillot === j.id) setLavages(await getMaillotsJoueur(j.id));
    } catch(err) { showToast(err.message, 'error'); }
  }

  async function retirerLavage(l, jid) {
    if (!window.confirm(`Retirer ce tour du ${l.date} ?`)) return;
    await deleteLavage(l.id);
    showToast('Tour retiré'); load();
    setLavages(await getMaillotsJoueur(jid));
  }

  function copyAcces(c) {
    const nomClub = getCurrentUser()?.club_nom || 'Équipote';
    const txt = `${nomClub} — tes accès\n\nAdresse : ${window.location.origin}\nIdentifiant : ${c.username}\nMot de passe : ${c.password}`;
    navigator.clipboard?.writeText(txt);
    showToast('Copié');
  }

  const actifs   = joueurs.filter(j => j.actif);
  const inactifs = joueurs.filter(j => !j.actif);

  // Formulaire d'un joueur : en haut pour un nouveau joueur, sous sa fiche pour une modification
  const formulaireJoueur = integre => (
    <div className={integre ? undefined : 'card'} style={integre
      ? { marginTop:12, paddingTop:12, borderTop:'1px solid var(--border)' } : { marginBottom:14 }}>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--accent)', marginBottom:14, letterSpacing:1, textTransform:'uppercase' }}>
              {editing ? 'Modifier' : 'Nouveau joueur'}
            </div>
            <form onSubmit={submit}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group" style={{ margin:0 }}><label>Prénom</label>
                  <input type="text" value={form.prenom||''} onChange={e=>setForm({...form,prenom:e.target.value})} required /></div>
                <div className="form-group" style={{ margin:0 }}><label>Nom</label>
                  <input type="text" value={form.nom||''} onChange={e=>setForm({...form,nom:e.target.value})} required /></div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:10 }}>
                <div className="form-group" style={{ margin:0 }}><label>N° Maillot</label>
                  <input type="number" value={form.numero||''} onChange={e=>setForm({...form,numero:e.target.value})} /></div>
                <div className="form-group" style={{ margin:0 }}><label>Naissance</label>
                  <input type="date" value={form.date_naissance||''} onChange={e=>setForm({...form,date_naissance:e.target.value})} /></div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:10 }}>
                <div className="form-group" style={{ margin:0 }}><label>Poste principal</label>
                  <select value={form.poste||''} onChange={e=>setForm({...form,poste:e.target.value})}>
                    <option value="">—</option>{POSTES.map(p=><option key={p}>{p}</option>)}
                  </select></div>
                <div className="form-group" style={{ margin:0 }}><label>Poste secondaire</label>
                  <select value={form.poste2||''} onChange={e=>setForm({...form,poste2:e.target.value})}>
                    <option value="">—</option>
                    {POSTES.filter(p => p !== form.poste).map(p=><option key={p}>{p}</option>)}
                  </select></div>
              </div>
              {moinsDe15(form.date_naissance) && (
                <div style={{ background:'rgba(59,130,246,.10)', border:'1px solid rgba(59,130,246,.35)', borderRadius:10,
                              padding:'11px 13px', marginBottom:12, fontSize:12.5, lineHeight:1.6, color:'#bfdbfe' }}>
                  <b>Joueur de moins de 15 ans.</b> Son compte est rattaché à son responsable légal : c'est lui qui reçoit
                  les accès et les convocations, et qui doit valider les conditions avant que le compte soit utilisable.
                  La famille choisit ensuite si l'enfant utilise son propre téléphone ou celui du parent.
                </div>)}
              {moinsDe15(form.date_naissance) && (
                <div className="form-group"><label>Email du responsable légal *</label>
                  <input type="email" value={form.email_parent||''} onChange={e=>setForm({...form,email_parent:e.target.value})}
                    required placeholder="parent@email.fr" /></div>)}
              <div className="form-group"><label>Email {moinsDe15(form.date_naissance) ? "de l'enfant (facultatif)" : !editing && form.creer_compte && '(pour ses accès)'}</label>
                <input type="email" value={form.email||''} onChange={e=>setForm({...form,email:e.target.value})}
                  required={!editing && form.creer_compte && !moinsDe15(form.date_naissance)} placeholder="joueur@email.fr" /></div>
              <div className="form-group"><label>Téléphone</label>
                <input type="tel" value={form.telephone||''} onChange={e=>setForm({...form,telephone:e.target.value})} /></div>

              {attribuables.length > 1 && (
                <div className="form-group">
                  <label>Droits</label>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {attribuables.map(r => (
                      <button key={r.key} type="button" onClick={()=>setForm({...form, role:r.key})}
                        style={{ textAlign:'left', padding:'9px 12px', borderRadius:8, cursor:'pointer',
                          border:`1px solid ${form.role===r.key ? r.color : 'rgba(255,255,255,0.08)'}`,
                          background: form.role===r.key ? r.color+'14' : '#161b22' }}>
                        <div style={{ fontSize:13, fontWeight:700, color: form.role===r.key ? r.color : '#e6edf3' }}>{r.label}</div>
                        <div style={{ fontSize:11, color:'#8b949e', marginTop:1 }}>{r.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!editing && (
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', marginBottom:14, fontSize:13 }}>
                  <input type="checkbox" checked={form.creer_compte !== false}
                    onChange={e=>setForm({...form, creer_compte:e.target.checked})}
                    style={{ width:16, height:16, accentColor:'var(--accent)' }} />
                  Créer son compte et lui envoyer ses accès par email
                </label>
              )}

              <div style={{ display:'flex', gap:10 }}>
                <button type="submit" className="btn btn-primary">{editing ? 'Enregistrer' : 'Créer'}</button>
                <button type="button" className="btn btn-secondary" onClick={()=>{setShowForm(false);setEditing(null);}}>Annuler</button>
              </div>
            </form>
          </div>
  );

  return (
    <>
      <div className="page-header">
        <div className="eyebrow">Gestion équipe</div>
        <h1>👥 Équipe</h1>
        <div className="sub">{actifs.length} joueur{actifs.length>1?'s':''}</div>
      </div>

      <div className="tabs">
        {[['effectif','👥 Effectif'],['maillots','🧺 Maillots']].map(([k,l]) => (
          <button key={k} className={`tab-btn ${vue===k?'active':''}`}
            onClick={()=>{setVue(k);setShowForm(false);setSelMaillot(null);setAutreChoix(false);
              if (k === 'maillots') getContexteMaillots().then(setCtxMaillots).catch(()=>setCtxMaillots(null));}}>{l}</button>
        ))}
      </div>

      <div className="section">
        {vue === 'effectif' && isAdmin && !showForm && (
          <button className="btn btn-primary" style={{ marginBottom:14 }} onClick={openNew}>+ Ajouter un joueur</button>
        )}

        {vue === 'effectif' && created && (
          <div className="card" style={{ marginBottom:14, borderColor:'#22c55e', background:'rgba(34,197,94,0.06)' }}>
            <div style={{ fontSize:12, fontWeight:700, color:'#22c55e', letterSpacing:1, textTransform:'uppercase', marginBottom:10 }}>
              {created.reset ? '🔄 Nouveau mot de passe' : '✅ Compte créé'}
            </div>
            <div style={{ background:'#0d1117', borderRadius:8, padding:'12px 14px', fontFamily:'monospace', fontSize:13, lineHeight:1.8 }}>
              <div><span style={{ color:'#8b949e' }}>Identifiant :</span> <b>{created.username}</b></div>
              <div><span style={{ color:'#8b949e' }}>Mot de passe :</span> <b style={{ color:'#22c55e' }}>{created.password}</b></div>
            </div>
            {created.email
              ? <div style={{ fontSize:11, color:'#22c55e', marginTop:8 }}>📧 Envoyé à {created.email}</div>
              : <div style={{ fontSize:11, color:'#eab308', marginTop:8 }}>⚠️ Pas d'email — transmets-lui ces accès</div>}
            <div style={{ display:'flex', gap:8, marginTop:12 }}>
              <button className="btn btn-secondary" onClick={()=>copyAcces(created)}>📋 Copier</button>
              <button className="btn btn-secondary" onClick={()=>{setCreated(null);setShowForm(false);}}>Fermer</button>
            </div>
          </div>
        )}

        {vue === 'effectif' && showForm && isAdmin && !editing && formulaireJoueur(false)}

        {vue === 'effectif' && actifs.map(j => {
          const ri = roleInfo(j.role);
          return (
            <div className="card" key={j.id} id={`joueur-${j.id}`} style={{ marginBottom:8, borderColor: showForm && editing?.id === j.id ? 'var(--accent)' : undefined }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:42, height:42, background:'var(--accent)', borderRadius:10, display:'flex',
                  alignItems:'center', justifyContent:'center', fontSize:17, fontWeight:800,
                  fontFamily:'Barlow Condensed', flexShrink:0, color:'var(--accent-text)' }}>{j.numero || '?'}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
                    <span style={{ fontSize:15, fontWeight:700 }}>{j.prenom} {j.nom}</span>
                    {ri && j.username && (
                      <span style={{ fontSize:9, fontWeight:700, color:ri.color, background:ri.color+'1f',
                        border:`1px solid ${ri.color}4d`, borderRadius:5, padding:'2px 7px', textTransform:'uppercase' }}>
                        {ri.label}
                      </span>
                    )}
                    {!j.username && <span style={{ fontSize:9, color:'#8b949e', border:'1px solid rgba(255,255,255,0.08)', borderRadius:5, padding:'2px 7px' }}>Sans compte</span>}
                  </div>
                  <div style={{ fontSize:11, color:'#8b949e', marginTop:3 }}>
                    {j.poste && (
                      <span style={{ marginRight:10 }}>
                        🏃 {j.poste}
                        {j.poste2 && <span style={{ color:'#6b7280' }}> / {j.poste2}</span>}
                      </span>
                    )}
                    {j.username && <span style={{ marginRight:10 }}>🔑 {j.username}</span>}
                  </div>
                  {j.email && <div style={{ fontSize:11, color:'#8b949e' }}>✉️ {j.email}</div>}
                </div>
              </div>
              {isAdmin && (
                <div style={{ display:'flex', gap:6, marginTop:11, flexWrap:'wrap' }}>
                  <button className="btn btn-secondary" style={{ width:'auto', padding:'7px 12px', fontSize:11 }} onClick={()=>openEdit(j)} aria-expanded={showForm && editing?.id === j.id}>{showForm && editing?.id === j.id ? '✕ Fermer' : '✏️ Modifier'}</button>
                  {j.username
                    ? <button className="btn btn-secondary" style={{ width:'auto', padding:'7px 12px', fontSize:11 }} onClick={()=>resetMdp(j)}>🔑 Reset MDP</button>
                    : <button className="btn btn-secondary" style={{ width:'auto', padding:'7px 12px', fontSize:11 }} onClick={()=>creerCompte(j)}>➕ Créer compte</button>}
                  {isSuper && <button className="btn btn-danger" style={{ width:'auto', padding:'7px 12px', fontSize:11 }} onClick={()=>supprimer(j)}>🗑️ Retirer</button>}
                </div>
              )}
              {isAdmin && showForm && editing?.id === j.id && formulaireJoueur(true)}
            </div>
          );
        })}
        {vue === 'effectif' && inactifs.length > 0 && (
          <div style={{ marginTop:22 }}>
            <button onClick={()=>setShowInactifs(!showInactifs)}
              style={{ background:'none', border:'none', color:'#8b949e', cursor:'pointer', fontSize:12,
                fontWeight:700, letterSpacing:1, textTransform:'uppercase', padding:'8px 0' }}>
              {showInactifs ? '▼' : '▶'} Anciens joueurs ({inactifs.length})
            </button>
            {showInactifs && inactifs.map(j => (
              <div className="card" key={j.id} style={{ marginBottom:8, opacity:0.55 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:38, height:38, background:'#21262d', borderRadius:10, display:'flex',
                    alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:800,
                    fontFamily:'Barlow Condensed', color:'#8b949e', flexShrink:0 }}>{j.numero || '?'}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700 }}>{j.prenom} {j.nom}</div>
                    <div style={{ fontSize:11, color:'#8b949e' }}>Retiré de l'effectif</div>
                  </div>
                  {isAdmin && (
                    <button className="btn btn-secondary" style={{ width:'auto', padding:'7px 12px', fontSize:11 }}
                      onClick={()=>reactiver(j)}>↩️ Réactiver</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── MAILLOTS ── */}
        {vue === 'maillots' && (() => {
          const tries = [...actifs].sort((a,b) =>
            (a.nb_lavages||0) - (b.nb_lavages||0) ||
            (a.dernier_lavage||'').localeCompare(b.dernier_lavage||'') ||
            a.nom.localeCompare(b.nom));
          const total = actifs.reduce((s,j) => s + (j.nb_lavages||0), 0);
          const maxi  = Math.max(1, ...actifs.map(j => j.nb_lavages||0));
          // Seuls les joueurs présents au match sont candidats (tout l'effectif, faute de match)
          const presents = ctxMaillots?.presents ? new Set(ctxMaillots.presents) : null;
          const estPresent = j => !presents || presents.has(j.id);
          const candidats = tries.filter(estPresent);
          const prochain = candidats[0];
          const match = ctxMaillots?.rencontre;
          const dateMatch = match ? new Date(match.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long' }) : '';

          return (
            <>
              {prochain && (
                <div className="card" style={{ marginBottom:14, borderColor:'#3b82f6',
                  background:'rgba(59,130,246,0.07)' }}>
                  <div style={{ fontSize:10, fontWeight:700, color:'#3b82f6', letterSpacing:2,
                    textTransform:'uppercase', marginBottom:6 }}>
                    Prochain sur la liste{match && presents ? ', parmi les présents' : ''}
                  </div>
                  {match && (
                    <div style={{ fontSize:12, color:'#8b949e', marginBottom:8 }}>
                      {match.aujourdhui ? 'Match du jour' : match.passe ? 'Dernier match' : 'Prochain match'} : vs {match.adversaire}, {dateMatch}
                      {!presents && ' — présences non renseignées, tout l\'effectif est proposé'}
                    </div>)}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    gap:10, flexWrap:'wrap' }}>
                    <div>
                      <div style={{ fontFamily:'Barlow Condensed', fontSize:22, fontWeight:800 }}>
                        {prochain.prenom} {prochain.nom}
                      </div>
                      <div style={{ fontSize:11.5, color:'#8b949e', marginTop:2 }}>
                        {prochain.nb_lavages > 0
                          ? `${prochain.nb_lavages} tour${prochain.nb_lavages>1?'s':''} · dernier le ${prochain.dernier_lavage}`
                          : "N'a encore jamais pris les maillots"}
                      </div>
                    </div>
                    {isAdmin && (
                      <button className="btn btn-primary" style={{ width:'auto', padding:'11px 18px',
                        background:'#3b82f6', whiteSpace:'nowrap' }}
                        onClick={()=>ajouterLavage(prochain)}>🧺 Il les prend</button>
                    )}
                  </div>
                  {isAdmin && candidats.length > 1 && (
                    !autreChoix
                      ? <button type="button" onClick={()=>setAutreChoix(true)}
                          style={{ marginTop:10, background:'none', border:'none', color:'#3b82f6', cursor:'pointer', fontSize:12.5, textDecoration:'underline', padding:0 }}>
                          Pas lui ? Choisir un autre joueur</button>
                      : <div style={{ marginTop:10, display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                          <select aria-label="Autre joueur" defaultValue="" onChange={e => { const j = candidats.find(x => x.id === Number(e.target.value)); if (j) ajouterLavage(j); }}
                            style={{ padding:'8px 10px', borderRadius:8, background:'var(--bg)', color:'var(--text)', border:'1px solid var(--border)', fontSize:13 }}>
                            <option value="" disabled>Qui prend les maillots ?</option>
                            {candidats.slice(1).map(j => <option key={j.id} value={j.id}>{j.prenom} {j.nom} — {j.nb_lavages||0} tour{(j.nb_lavages||0)>1?'s':''}</option>)}
                          </select>
                          <button type="button" onClick={()=>setAutreChoix(false)} style={{ background:'none', border:'none', color:'#8b949e', cursor:'pointer', fontSize:12 }}>Annuler</button>
                        </div>
                  )}
                </div>
              )}
              {!prochain && presents && (
                <div className="card" style={{ marginBottom:14, fontSize:13, color:'#8b949e' }}>
                  Aucun joueur marqué présent au match{match ? ` contre ${match.adversaire}` : ''} : renseignez les présences, ou choisissez sur la liste ci-dessous.
                </div>
              )}

              <div style={{ fontSize:11.5, color:'#8b949e', marginBottom:12, lineHeight:1.6 }}>
                Classement du moins souvent au plus souvent. {total} tour{total>1?'s':''} au total.
              </div>

              {tries.map((j, i) => {
                const nb = j.nb_lavages || 0;
                const ouvert = selMaillot === j.id;
                return (
                  <div key={j.id}>
                    <div className="card" onClick={()=>openMaillots(j)}
                      style={{ cursor:'pointer', marginBottom: ouvert ? 0 : 8,
                        background: ouvert ? 'rgba(59,130,246,0.06)' : undefined,
                        borderColor: i===0 && nb === (tries[0].nb_lavages||0) ? 'rgba(59,130,246,0.3)' : undefined }}>
                      <div style={{ display:'flex', alignItems:'center', gap:11 }}>
                        <span className="num-badge" style={{ color:'#3b82f6' }}>{j.numero||'—'}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:14, fontWeight:700, opacity: estPresent(j) ? 1 : .55 }}>{j.prenom} {j.nom}
                            {!estPresent(j) && <span style={{ fontSize:10.5, fontWeight:600, color:'#8b949e', marginLeft:6 }}>absent au match</span>}</div>
                          <div style={{ fontSize:11, color:'#8b949e', marginTop:2 }}>
                            {nb > 0 ? `Dernier tour le ${j.dernier_lavage}` : 'Jamais pris les maillots'}
                          </div>
                          <div style={{ marginTop:6, height:4, background:'#21262d', borderRadius:3,
                            overflow:'hidden', maxWidth:180 }}>
                            <div style={{ width:`${(nb/maxi)*100}%`, height:'100%',
                              background:'#3b82f6', borderRadius:3 }} />
                          </div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0 }}>
                          <div style={{ fontFamily:'Barlow Condensed', fontSize:24, fontWeight:800,
                            color: nb === 0 ? '#8b949e' : '#3b82f6', lineHeight:1 }}>{nb}</div>
                          <div style={{ fontSize:9, color:'#8b949e', letterSpacing:1 }}>
                            TOUR{nb>1?'S':''}
                          </div>
                        </div>
                        {isAdmin && (
                          <button onClick={(e)=>{e.stopPropagation();ajouterLavage(j);}}
                            title={`${j.prenom} prend les maillots`}
                            style={{ background:'rgba(59,130,246,0.12)', border:'1px solid rgba(59,130,246,0.4)',
                              borderRadius:8, color:'#3b82f6', cursor:'pointer', fontSize:12, fontWeight:700,
                              padding:'7px 9px', flexShrink:0, whiteSpace:'nowrap' }}>🧺 Il les prend</button>
                        )}
                      </div>
                    </div>

                    {ouvert && (
                      <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)',
                        borderTop:'none', borderRadius:'0 0 10px 10px', padding:'10px 14px', marginBottom:8 }}>
                        {lavages.length === 0
                          ? <div style={{ color:'#8b949e', fontSize:13, padding:'6px 0' }}>Aucun tour enregistré</div>
                          : lavages.map(l => (
                            <div key={l.id} style={{ display:'flex', justifyContent:'space-between',
                              alignItems:'center', gap:10, padding:'7px 0',
                              borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                              <div>
                                <div style={{ fontSize:12.5 }}>🧺 {l.date}</div>
                                {l.cree_par && <div style={{ fontSize:10.5, color:'#8b949e', marginTop:1 }}>
                                  noté par {l.cree_par}</div>}
                              </div>
                              {isAdmin && (
                                <button onClick={()=>retirerLavage(l, j.id)}
                                  style={{ background:'none', border:'1px solid rgba(239,68,68,0.35)',
                                    borderRadius:6, color:'#ef4444', cursor:'pointer',
                                    fontSize:11, padding:'4px 7px' }}>🗑</button>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          );
        })()}
      </div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
